import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOWrapper } from "@/components/SEOWrapper";
import { AuthorBox } from "@/components/insight/AuthorBox";
import { InsightBody, slugify } from "@/components/insight/InsightBody";
import { usePageSeo } from "@/hooks/usePageSeo";
import { trackArticleView, trackResourceCta, trackServiceFromContent } from "@/lib/analytics";
import type { InsightContent } from "@/data/insights/types";
import { insights } from "@/data/insights";
import { KNOWLEDGE_TOPICS, topicForArticle } from "@/data/knowledgeGraph";
import { ArrowRight } from "lucide-react";
import portrait from "@/assets/luis-roc-profile.jpg";
import office from "@/assets/luis-roc-office.jpg";
import {
  Crumbs,
  ReadingColumn,
  ReadingProgress,
  SectionIndex,
  SectionStrip,
} from "@/components/luminous/interior";

/** Cluster editorial → visualización conceptual de búsqueda. */
const CLUSTER_VISUAL: Record<string, string> = {
  "Technical SEO": "technical-seo",
  "AI Search": "ai-search",
  "International SEO": "international-seo",
  "Hispanic Search": "hispanic-seo",
  "Ecommerce SEO": "ecommerce-seo",
  "Amazon Search": "amazon-seo",
  "SEO Strategy": "seo-consultant",
};

const LIBRARY = {
  es: { hub: "/biblioteca", label: "Search Library", crumb: "Biblioteca" },
  en: { hub: "/en/library", label: "Search Library", crumb: "Library" },
} as const;

const FORMAT_LABEL: Record<string, { es: string; en: string }> = {
  insight: { es: "Análisis", en: "Insight" },
  guide: { es: "Guía", en: "Guide" },
  playbook: { es: "Playbook", en: "Playbook" },
  research: { es: "Research", en: "Research" },
};

const formatDate = (iso: string, language: "es" | "en") =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(language === "es" ? "es-ES" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

const ARTICLE_ID = "insight-article";

/**
 * Plantilla editorial única de la Search Library, en la dirección luminosa.
 * Idioma derivado de la URL; canonical autorreferencial y hreflang recíprocos.
 */
export const InsightArticle = ({ content }: { content: InsightContent }) => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const t = content[language];

  // "Fuentes y contexto": servicio del tema, metodología, otro artículo del
  // mismo tema o de un tema relacionado y la página del autor (2-5 enlaces).
  const topic = topicForArticle(content.id);
  const sources: { label: string; href: string }[] = [];
  if (topic) {
    const es = language === "es";
    sources.push({
      label: es ? `Servicio de ${topic.nameEs}` : `${topic.nameEn} service`,
      href: es ? topic.canonicalServiceUrlEs : topic.canonicalServiceUrlEn,
    });
    sources.push({ label: "ROC Search Growth Framework", href: es ? "/metodologia" : "/en/methodology" });
    const relatedIds = [
      ...topic.supportingArticles,
      ...topic.relatedTopics.flatMap((id) => KNOWLEDGE_TOPICS.find((k) => k.id === id)?.supportingArticles ?? []),
    ].filter((id) => id !== content.id);
    const other = insights.find((i) => i.id === relatedIds[0]);
    if (other) sources.push({ label: other[language].title, href: other.path[language] });
    sources.push({ label: es ? "Sobre Luis ROC, autor" : "About Luis ROC, author", href: es ? "/sobre-mi" : "/en/about" });
  }
  const lib = LIBRARY[language];

  useEffect(() => {
    trackArticleView(content.id, content.cluster, content.format);
  }, [content.id, content.cluster, content.format]);

  const headings = t.body.filter((b) => b.type === "h2" && b.text);
  const showToc = headings.length >= 3;
  const tocItems = headings.map((h) => ({ id: slugify(h.text ?? ""), label: h.text ?? "" }));
  const tocLabel = language === "es" ? "Contenido" : "Contents";

  return (
    <SEOWrapper
      title={t.title}
      description={t.description}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      pageType="article"
      localBusiness={false}
      summary={t.summary}
      structuredData={{
        type: "article",
        data: {
          "@type": content.format === "research" ? "Article" : "BlogPosting",
          headline: t.h1,
          description: t.description,
          inLanguage: language,
          datePublished: content.published,
          dateModified: content.updated,
          mainEntityOfPage: url,
          articleSection: content.cluster,
          topics: [content.cluster],
          keywords: content.cluster,
        },
      }}
    >
      <div className="v3-dark min-h-screen">
        <Header language={language} onLanguageChange={onLanguageChange} />
        <ReadingProgress targetId={ARTICLE_ID} />

        <main>
          {/* Encabezado interior compacto: no repite el hero de la Home */}
          {/* Portada editorial: título y resumen a la izquierda, ficha del
              documento en una columna de datos. No es una landing de servicio. */}
          <header className="v3-dark-hero border-b border-[color:var(--v3-line)]">
            <div className="v3-shell pb-16 pt-8 lg:pb-24">
              <Crumbs items={[{ label: lib.crumb, to: lib.hub }, { label: t.h1 }]} />
              <div className="mt-12 grid items-end gap-12 lg:grid-cols-12 lg:gap-16">
                <div className="min-w-0 lg:col-span-7">
                  <p className="v3-ed-label">
                    {FORMAT_LABEL[content.format][language]}
                    <span className="mx-2" aria-hidden>/</span>
                    {t.eyebrow}
                  </p>
                  <h1
                    className="mt-6 max-w-[18ch] font-bold text-[color:var(--v3-ink)]"
                    style={{ fontSize: "clamp(2.5rem, 5.4vw, 4.75rem)", lineHeight: 1.02, letterSpacing: "-0.04em" }}
                  >
                    {t.h1}
                  </h1>
                  <p className="mt-8 max-w-[56ch] text-[1.125rem] leading-[1.65] text-[color:var(--v3-ink-soft)]">
                    {t.summary}
                  </p>
                </div>

                <aside className="min-w-0 lg:col-span-5">
                  <div className="v3-ed-card overflow-hidden">
                    <div className="grid grid-cols-[112px_1fr] gap-5 p-5 sm:grid-cols-[136px_1fr]">
                      <div className="v3-ed-portrait aspect-[4/5]">
                        <img src={portrait} alt="Luis ROC" width={272} height={340} className="h-full w-full object-cover grayscale-[20%]" />
                      </div>
                      <div className="flex min-w-0 flex-col justify-between py-1">
                        <div>
                          <p className="v3-ed-label">{language === "es" ? "Autor" : "Author"}</p>
                          <Link to={language === "es" ? "/sobre-mi" : "/en/about"} className="mt-2 block text-xl font-bold tracking-[-0.02em] text-[color:var(--v3-ink)] hover:underline underline-offset-4">
                            Luis ROC
                          </Link>
                          <p className="mt-1 text-sm text-[color:var(--v3-muted)]">
                            {language === "es" ? "Consultor de SEO y Search Growth" : "SEO and Search Growth consultant"}
                          </p>
                        </div>
                        <p className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-[color:var(--v3-line-strong)] px-3 py-1 text-xs text-[color:var(--v3-ink)]">
                          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[color:var(--v3-copper)]" />
                          {language === "es" ? "25+ años de experiencia" : "25+ years of experience"}
                        </p>
                      </div>
                    </div>
                    <dl className="grid grid-cols-3 border-t border-[color:var(--v3-line)]">
                      <div className="border-r border-[color:var(--v3-line)] p-4">
                        <dt className="v3-ed-label">{language === "es" ? "Lectura" : "Reading"}</dt>
                        <dd className="mt-1.5 text-sm font-medium text-[color:var(--v3-ink)]">{content.readingMinutes} min</dd>
                      </div>
                      <div className="border-r border-[color:var(--v3-line)] p-4">
                        <dt className="v3-ed-label">{language === "es" ? "Publicado" : "Published"}</dt>
                        <dd className="mt-1.5 text-sm font-medium text-[color:var(--v3-ink)]">
                          <time dateTime={content.updated}>{formatDate(content.updated, language)}</time>
                        </dd>
                      </div>
                      <div className="p-4">
                        <dt className="v3-ed-label">{language === "es" ? "Tema" : "Topic"}</dt>
                        <dd className="mt-1.5 truncate text-sm font-medium text-[color:var(--v3-ink)]">{content.cluster}</dd>
                      </div>
                    </dl>
                  </div>
                </aside>
              </div>
            </div>
          </header>

          {/* Índice compacto en móvil */}
          {showToc && <SectionStrip items={tocItems} label={tocLabel} />}

          <div className="v3-shell v3-section-tight">
            <div className={showToc ? "grid gap-12 lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-16" : ""}>
              <article id={ARTICLE_ID} className="min-w-0">
                <ReadingColumn>
                  <InsightBody blocks={t.body} />

                  {/* CTA contextual único, al final */}
                  <section className="v3-ed-card mt-20 overflow-hidden">
                    <div className="grid md:grid-cols-[1fr_220px]">
                      <div className="p-8 sm:p-10">
                        <p className="v3-ed-label">{language === "es" ? "Siguiente paso" : "Next step"}</p>
                        <p className="mt-5 text-[1.35rem] font-semibold leading-[1.35] tracking-[-0.02em] text-[color:var(--v3-ink)]">{t.cta.text}</p>
                        <Link
                          to={t.cta.href}
                          onClick={() => {
                            trackResourceCta(content.id, t.cta.href);
                            trackServiceFromContent(content.id, t.cta.href);
                          }}
                          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[color:var(--v3-blue)] px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[color:var(--v3-blue-deep)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--v3-ink)]"
                        >
                          {t.cta.label}
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </div>
                      <div className="relative hidden md:block">
                        <img src={office} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-80" />
                      </div>
                    </div>
                  </section>

                  {t.related.length > 0 && (
                    <section className="mt-14 border-t border-[color:var(--v3-line)] pt-8">
                      <p className="v3-ed-label">
                        {language === "es" ? "Seguir leyendo" : "Keep reading"}
                      </p>
                      <ul className="mt-4 space-y-2">
                        {t.related.map((r) => (
                          <li key={r.href}>
                            <Link to={r.href} className="v3-inline-link">
                              {r.label}
                              <ArrowRight className="h-4 w-4" aria-hidden />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {sources.length > 0 && (
                    <section
                      className="mt-12 border-t border-[color:var(--v3-line)] pt-6"
                      aria-label={language === "es" ? "Fuentes y contexto dentro de LuisROC.com" : "Sources and context on LuisROC.com"}
                    >
                      <p className="v3-micro">
                        {language === "es" ? "Fuentes y contexto dentro de LuisROC.com" : "Sources and context on LuisROC.com"}
                      </p>
                      <ul className="mt-4 space-y-2">
                        {sources.map((r) => (
                          <li key={r.href}>
                            <Link to={r.href} className="v3-inline-link">
                              {r.label}
                              <ArrowRight className="h-4 w-4" aria-hidden />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <AuthorBox language={language} />
                </ReadingColumn>
              </article>

              {showToc && (
                <aside className="hidden lg:block">
                  <div className="sticky top-[calc(var(--v3-header-h)+2rem)]">
                    <SectionIndex items={tocItems} label={tocLabel} />
                  </div>
                </aside>
              )}
            </div>
          </div>
        </main>

        <Footer language={language} />
      </div>
    </SEOWrapper>
  );
};
